/*
 * Sold-out popup.
 *
 * Intercepts every checkout button on the page via delegation, fires the InitiateCheckout pixel,
 * opens the capture popup, and tags the submission with the first product in the cart.
 *
 * The form itself is never intercepted — it posts natively so the browser navigates and Shopify
 * writes the customer record server-side.
 */
(function () {
  const CHECKOUT_SELECTOR = '[name="checkout"]';
  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const BASE_TAG = 'uncommon-air';

  let popup = null;
  let panel = null;
  let lastTrigger = null;

  function isOpen() {
    return popup !== null && !popup.hidden;
  }

  function focusableItems() {
    return Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE_SELECTOR));
  }

  function open(trigger) {
    if (!popup || isOpen()) return;
    lastTrigger = trigger || null;
    popup.hidden = false;

    /* Prefer the email field (or the confirmation message) over the close button. */
    const preferred = panel.querySelector('[data-sold-out-popup-focus]');
    const items = focusableItems();
    (preferred || items[0] || panel).focus();
  }

  function close() {
    if (!isOpen()) return;
    popup.hidden = true;
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    lastTrigger = null;
  }

  function slugify(value) {
    return String(value)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /*
   * Enriches the hidden tags field with the cart's first product. Deliberately not awaited by the
   * click handler: the popup is already open by the time this runs, so a slow or failing /cart.js
   * can neither delay the popup nor stop the email from being captured. On any failure the field
   * keeps the base tag it was rendered with.
   */
  function appendCartTag() {
    const tags = popup.querySelector('[data-sold-out-popup-tags]');
    if (!tags) return;

    fetch('/cart.js')
      .then(function (response) {
        if (!response.ok) throw new Error('Cart request failed: ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        const firstItem = cart && cart.items && cart.items[0];
        const slug = firstItem ? slugify(firstItem.product_title) : '';
        if (slug) tags.value = BASE_TAG + ',' + slug;
      })
      .catch(function () {
        /* Leave the base tag in place. */
      });
  }

  function onDocumentClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest(CHECKOUT_SELECTOR);
    if (!trigger) return;

    event.preventDefault();

    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout');
    }

    open(trigger);
    appendCartTag();
  }

  function onPopupClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target === popup) {
      close();
      return;
    }

    if (target.closest('[data-sold-out-popup-close]')) {
      event.preventDefault();
      close();
    }
  }

  function onKeydown(event) {
    if (!isOpen()) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    const items = focusableItems();
    if (items.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === panel || !panel.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  function init() {
    popup = document.querySelector('[data-sold-out-popup]');
    if (!popup) return;

    panel = popup.querySelector('[data-sold-out-popup-panel]');
    if (!panel) return;

    /*
     * Capture phase so the interception lands before any handler bound to the button itself,
     * and on document so cart notification, cart page, and anything added later are all covered.
     */
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('keydown', onKeydown);
    popup.addEventListener('click', onPopupClick);

    /* Shopify redirects back here after a successful post; reopen in confirmation state. */
    if (popup.dataset.soldOutPopupPosted === 'true') open(null);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
